import { useCallback, useState } from 'react';

import { useAppDispatch } from 'src/app/store/hooks';
import type {
  InvoiceExportCSVArg,
  InvoiceSelectedListExportCSVArg,
} from 'src/common/external/bambi-api/emptyApi';
import {
  useLazyGetInvoicesExportListAsCsvQuery,
  useLazyGetInvoicesExportListBySelectedInvoiceCsvQuery,
  useLazyGetInvoicesPaymentsExportListBySelectedInvoiceCsvQuery,
  useLazyGetInvoicesRefundsExportListBySelectedInvoiceCsvQuery,
} from 'src/common/external/bambi-api/emptyApi';
import formatServerError from 'src/common/util/serverErrorFormatter';
import {
  setExportedCondensedReportData,
  setExportedInvoicePaymentsData,
  setExportedInvoiceRefundsData,
} from 'src/features/billing/invoices/invoice.slice';
import type {
  InvoiceExportCondensedReport,
  InvoicePaymentRow,
  InvoiceRefundRow,
} from 'src/features/billing/invoices/types';

export function useLazyFetchInvoiceExportData() {
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();
  const [data, setData] = useState<InvoiceExportCondensedReport>();
  const [fetchInvoiceExport] = useLazyGetInvoicesExportListAsCsvQuery();
  const [fetchInvoiceExportByInvoiceId] =
    useLazyGetInvoicesExportListBySelectedInvoiceCsvQuery();
  const [fetchInvoicePaymentsExport] =
    useLazyGetInvoicesPaymentsExportListBySelectedInvoiceCsvQuery();
  const [fetchInvoiceRefundsExport] =
    useLazyGetInvoicesRefundsExportListBySelectedInvoiceCsvQuery();

  // Fetch related data by invoice id
  const fetchRelatedData = useCallback(
    async (invoiceIds: string[]) => {
      const results = await Promise.all([
        // payments
        fetchInvoicePaymentsExport({
          format: 'pdf',
          selectedInvoices: invoiceIds,
        }).unwrap(),
        // refunds
        fetchInvoiceRefundsExport({
          format: 'pdf',
          selectedInvoices: invoiceIds,
        }).unwrap(),
      ]);

      return {
        payments: results[0],
        refunds: results[1],
      };
    },
    [fetchInvoicePaymentsExport, fetchInvoiceRefundsExport]
  );

  // Fetch invoice data by query or by selected
  const trigger = useCallback(
    async (arg: InvoiceExportCSVArg | InvoiceSelectedListExportCSVArg) => {
      setLoading(true);
      setData(undefined);
      setError(undefined);

      const isSelectedListExport = 'selectedInvoices' in arg;

      try {
        const invoiceExport = (await (isSelectedListExport
          ? fetchInvoiceExportByInvoiceId(
              arg as InvoiceSelectedListExportCSVArg
            ).unwrap()
          : fetchInvoiceExport(arg as InvoiceExportCSVArg).unwrap())) as
          | InvoiceExportCondensedReport
          | undefined;

        if (!invoiceExport?.records.length) {
          setError('No invoices found');
          return;
        }

        // dispatch
        const invoiceIds = invoiceExport.records.map(
          (invoiceExport) => invoiceExport.InvoiceID
        );
        const relatedDataResults = await fetchRelatedData(invoiceIds);

        setData(invoiceExport);

        dispatch(
          setExportedCondensedReportData(
            invoiceExport as InvoiceExportCondensedReport
          )
        );
        dispatch(
          setExportedInvoicePaymentsData(
            relatedDataResults.payments?.records as InvoicePaymentRow[]
          )
        );
        dispatch(
          setExportedInvoiceRefundsData(
            relatedDataResults.refunds?.records as InvoiceRefundRow[]
          )
        );
      } catch (e) {
        const errMsg = `There was an error downloading the data. ${formatServerError(
          e
        )}`;
        setError(errMsg);
      } finally {
        setLoading(false);
      }
    },
    [
      dispatch,
      fetchInvoiceExport,
      fetchInvoiceExportByInvoiceId,
      fetchRelatedData,
    ]
  );

  return {
    loading,
    error,
    trigger,
    data,
  };
}
